import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'
import { Stack, Box } from '@mui/material';

import {Header} from 'components/Modal';

import ModelSteps, { Slider } from './components'
import StepOtrumWarning from './StepOtrumWarning'
import { useRoom } from '../../use-rooms'
import { useSync } from './hooks'
import { trackEvent } from 'analytics'

const LG = ({ deviceType, device, psk, pin }) => {
  const [step, setStep] = useState(0);
  const { t, i18n } = useTranslation();
  const room = useRoom()
  const navigate = useNavigate();
  const [syncTvsMacAddress, { loading: loadingSync }] = useSync()

  const titles = useMemo<string[]>(() => {
    return [
      deviceType === 'old' ? t('stream-cast.config.lg.old.description_title') : null,
      ...(device.wired
        ? [
          t('stream-cast.config.lg.new.block_1_wired_title')
        ]
        : [
          t('stream-cast.config.lg.new.block_1_wifi_title'),
          t('stream-cast.config.lg.new.block_2_wifi_title')
        ]
      ),
      t('stream-cast.config.lg.new.block_3_title'),
      t('stream-cast.config.otrum_warning_title'),
      t('stream-cast.config.otrum_title')
    ].filter(Boolean) as string[];
  }, [i18n.language, deviceType, device, room])

  const steps = useMemo<string[]>(() => {
    return [
      deviceType === 'old' ? t('stream-cast.config.lg.old.description') : null,
      ...(device.wired
        ? [
          t('stream-cast.config.lg.new.block_1_wired', { language: t(`languages.${i18n.language || 'fr'}`), country: t(`countries.${i18n.language || 'fr'}`) })
        ]
        : [
          t('stream-cast.config.lg.new.block_1_wifi', { language: t(`languages.${i18n.language || 'fr'}`), country: t(`countries.${i18n.language || 'fr'}`) }),
          t('stream-cast.config.lg.new.block_2_wifi', { psk: psk, ssid: room?.ssid_name })
        ]
      ),
      t('stream-cast.config.lg.new.block_3'),
      'never_show_replace_by_warning_step',
      t('stream-cast.config.otrum', {pin, room: device.subroom ? `${device.room} | ${device.subroom}` : device.room})
    ].filter(Boolean) as string[];
  }, [i18n.language, deviceType, device, room])

  const isWarningStep = useMemo<boolean>(() => {
    return step === steps.length - 2
  }, [step, steps])

  const handleSelect = async(value: number) => {
    if (value === 4 && room?.is_iot_format) {
      await syncTvsMacAddress();
    }
    if (value > steps.length - 1) {
      trackEvent('stream_cast_config_completed', { manufacturer: 'lg', device_type: deviceType })
      navigate('..')
    }
    setStep(value)
  }

  return (
    <Stack sx={{ width: 500, fontSize: 13 }}>
      <ModelSteps
        disabledNext={isWarningStep}
        nbSteps={steps.length}
        step={step}
        onSelect={handleSelect}
        loading={loadingSync}
      >
        <Header icon={<i className="fa-solid fa-gear" />} title={titles[step] || ''} />
        {
          isWarningStep
            ? <StepOtrumWarning onValidate={() => setStep(prev => prev + 1)} />
            : <Box dangerouslySetInnerHTML={{ __html: steps[step] || '' }} />
        }
      </ModelSteps>
      <Slider nbSteps={steps.length} step={step} onSelect={handleSelect} />
    </Stack>
  )
}

export default LG;